import axiosInstance from "./axiosInstance";

/**
 * All calls go through axiosInstance. They previously used raw axios/fetch with
 * hand-attached Authorization headers, which meant the 401 -> clear-token -> redirect
 * interceptor did NOT cover them — including fetchRankingsAPI, the console's single most
 * important call. A session expiring there surfaced as a generic thrown error instead of
 * sending the user back to sign in.
 */

export const scoreResumeAPI = async (resumeFile) => {
  try {
    const formData = new FormData();
    formData.append("resume", resumeFile);
    const response = await axiosInstance.post("resumes/", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    console.error("Error scoring resume:", error);
    throw new Error("Failed to score resume. Please try again.");
  }
};

export const uploadResumeAPI = async (jobId, files, onProgressUpdate) => {
  try {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const response = await axiosInstance.post(`jobs/${jobId}/resumes/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (progressEvent) => {
        if (!onProgressUpdate || !progressEvent.total) return;
        onProgressUpdate(
          Math.round((progressEvent.loaded * 100) / progressEvent.total)
        );
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to upload resumes:", error);
    throw new Error(error.response?.data?.message || "Failed to upload resumes.");
  }
};

export const handleScoreAPI = async (jobId) => {
  try {
    const response = await axiosInstance.post(`jobs/${jobId}/resumes/score`);
    return response.data;
  } catch (error) {
    console.error("Failed to score resumes:", error);
    throw new Error(error.response?.data?.message || "Failed to score resumes.");
  }
};

/**
 * Per-file pipeline status for a job's resumes. The upload modal polls this rather than the full
 * rankings payload — it wants to know where five files got to, not to pull every candidate's
 * parameters and screening state to render five spinners.
 */
export const fetchResumeStatusAPI = async (jobId) => {
  try {
    const response = await axiosInstance.get(`jobs/${jobId}/resumes/status`);
    return response.data.statuses;
  } catch (error) {
    console.error("Failed to fetch resume status:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch resume status.");
  }
};

/** Re-run the pipeline for a resume that failed or stalled. Reprocesses from S3 — no re-upload. */
export const retryResumeAPI = async (jobId, resumeId) => {
  try {
    const response = await axiosInstance.post(`jobs/${jobId}/resumes/${resumeId}/retry`);
    return response.data;
  } catch (error) {
    console.error("Failed to retry resume:", error);
    throw new Error(error.response?.data?.message || "Failed to retry resume.");
  }
};

export const fetchRankingsAPI = async (jobId) => {
  try {
    const response = await axiosInstance.get(`jobs/${jobId}/ranking/`);
    return response.data.rankings;
  } catch (error) {
    console.error("Failed to fetch rankings:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch rankings.");
  }
};

export const generatePeerToPeerReportAPI = async (jobId, topK) => {
  try {
    await axiosInstance.post(`rescore/${jobId}`, { top_k: topK });
  } catch (error) {
    console.error("Failed to generate report:", error);
    throw new Error(
      error.response?.data?.message || "Failed to generate peer-to-peer report."
    );
  }
};

export const selectCandidatesForInterviewAPI = async (jobId, selectedCandidates) => {
  try {
    const response = await axiosInstance.put(`jobs/${jobId}/selected_candidates`, {
      selected_candidates: selectedCandidates.map((c) => c.ResumeId),
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "An unknown error occurred");
  }
};

export const updateCandidateEmailAPI = async (jobId, candidateId, email) => {
  try {
    const response = await axiosInstance.put(`candidate/${jobId}/${candidateId}/`, {
      email,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update email:", error);
    throw new Error(
      error.response?.data?.message || "Failed to update email. Please try again."
    );
  }
};

// Phone lives on the resume's candidate_info, so it updates through the resume endpoint
// (keyed by resumeId), unlike email which goes through the candidate endpoint.
export const updateCandidatePhoneAPI = async (jobId, resumeId, phone) => {
  try {
    const response = await axiosInstance.put(`jobs/${jobId}/resumes/${resumeId}`, {
      phone,
    });
    return response.data;
  } catch (error) {
    console.error("Failed to update phone:", error);
    throw new Error(
      error.response?.data?.message || "Failed to update phone. Please try again."
    );
  }
};

/**
 * Semantic search across every resume in the company's pool.
 *
 * company_id is derived from the JWT server-side — passing it in the body does nothing,
 * so don't. (This endpoint used to have no auth at all: any anonymous caller could search
 * any company's resumes. That's fixed; it now requires a token, and a user with no company
 * association gets a 403.)
 */
export const ragSearchResume = async ({ query }) => {
  try {
    const response = await axiosInstance.post("rag", { query });
    return response.data;
  } catch (error) {
    console.error("Failed to Search Resumes:", error);
    throw new Error(error.response?.data?.message || "Failed to search resumes.");
  }
};

export const downloadResumeAPI = async (jobId, resumeId) => {
  const response = await axiosInstance.get(`jobs/${jobId}/resumes/${resumeId}/download`);
  return response.data.download_url;
};

export const getCandidateResume = async (jobId, resumeId) => {
  try {
    const response = await axiosInstance.get(`jobs/${jobId}/resumes/${resumeId}`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch resume:", error);
    throw new Error(error.response?.data?.message || "Failed to fetch resume.");
  }
};
